<template>
  <div v-show="hasAnyReviews">
    <!-- Header -->
    <div class="mb-8">
      <h2
        class="dark:text-content-inverse font-bold text-2xl text-content-level-900 tracking-tight"
      >
        {{ $t('commerce.storeReviews.ratingAndReviews') }}
      </h2>

      <div
        class="dark:text-content-level-400 flex gap-1.5 items-center mt-1 text-content-level-600 text-sm"
      >
        <svg
          class="dark:text-content-inverse h-4 text-content-level-900 w-4"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
          />
        </svg>

        <span class="dark:text-content-inverse font-medium text-content-level-900">
          {{ activeStats.average_rating.toFixed(1) }}
        </span>

        <span>·</span>

        <span>{{
          $t('commerce.storeReviews.valueReviews', { count: activeStats.total_reviews })
        }}</span>
      </div>
    </div>

    <!-- Source toggle -->
    <div
      v-if="hasNativeReviews && hasGoogleReviews"
      class="flex gap-2 mb-6"
    >
      <button
        type="button"
        class="cursor-pointer font-medium gap-1.5 inline-flex items-center px-4 py-2 rounded-full text-sm transition-colors"
        :class="[
          activeSource === ReviewSource.NATIVE
            ? 'bg-primary text-content-inverse'
            : 'bg-surface-level-100 text-content-level-700 hover:bg-surface-level-200 dark:bg-surface-level-800 dark:text-content-level-300 dark:hover:bg-surface-level-700'
        ]"
        @click="showNativeReviews"
      >
        {{ $t('commerce.storeReviews.mealzReviews') }}
      </button>

      <button
        type="button"
        class="cursor-pointer font-medium gap-1.5 inline-flex items-center px-4 py-2 rounded-full text-sm transition-colors"
        :class="[
          activeSource === ReviewSource.GOOGLE
            ? 'bg-primary text-content-inverse'
            : 'bg-surface-level-100 text-content-level-700 hover:bg-surface-level-200 dark:bg-surface-level-800 dark:text-content-level-300 dark:hover:bg-surface-level-700'
        ]"
        @click="showGoogleReviews"
      >
        <svg
          class="h-4 w-4"
          viewBox="0 0 24 24"
        >
          <path
            fill="currentColor"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
          />

          <path
            fill="currentColor"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />

          <path
            fill="currentColor"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />

          <path
            fill="currentColor"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          /></svg
        >{{ $t('commerce.storeReviews.googleReviews') }}
      </button>
    </div>

    <!-- Filter Tabs -->
    <div class="flex flex-wrap gap-2 mb-6">
      <button
        type="button"
        class="cursor-pointer font-medium px-4 py-2 rounded-full text-sm transition-colors"
        :class="[
          reviewsRatingFilter === null
            ? 'bg-primary text-content-inverse'
            : 'bg-surface-level-100 text-content-level-700 hover:bg-surface-level-200 dark:bg-surface-level-800 dark:text-content-level-300 dark:hover:bg-surface-level-700'
        ]"
        @click="clearRatingFilter"
      >
        {{ $t('commerce.storeReviews.all') }}
      </button>

      <button
        v-for="stars in [5, 4, 3, 2]"
        :key="`tab-${stars}`"
        type="button"
        class="cursor-pointer font-medium gap-1 inline-flex items-center px-4 py-2 rounded-full text-sm transition-colors"
        :class="[
          reviewsRatingFilter === stars
            ? 'bg-primary text-content-inverse'
            : 'bg-surface-level-100 text-content-level-700 hover:bg-surface-level-200 dark:bg-surface-level-800 dark:text-content-level-300 dark:hover:bg-surface-level-700'
        ]"
        :data-rating="stars"
        @click="setRatingFilterFromEvent"
      >
        <svg
          class="h-3.5 w-3.5"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
          />
        </svg>
        {{ stars }}
      </button>
    </div>

    <!-- Loading -->
    <div
      v-if="isCurrentSourceLoading && activeReviews.length === 0"
      class="py-12 text-center"
    >
      <div class="animate-spin border-b-2 border-primary h-8 mx-auto rounded-full w-8" />
    </div>

    <!-- Reviews Grid -->
    <div
      v-else
      class="gap-4 grid grid-cols-1 md:grid-cols-2"
    >
      <div
        v-for="review in activeReviews"
        :key="getReviewKey(review)"
        class="bg-surface-canvas border border-border-level-200 dark:bg-surface-level-800 dark:border-border-level-700 p-5 rounded-2xl"
      >
        <!-- Author name -->
        <p class="dark:text-content-inverse font-semibold text-base text-content-level-900">
          {{ review.user?.name || $t('commerce.storeReviews.anonymousReviewer') }}
        </p>

        <!-- Stars + date -->
        <div class="flex gap-1.5 items-center mt-1.5">
          <div class="flex gap-0.5 items-center">
            <svg
              v-for="star in 5"
              :key="`review-star-${getReviewKey(review)}-${star}`"
              class="h-4 w-4"
              :class="
                star <= (review.rating ?? 0)
                  ? 'text-content-level-900 dark:text-content-inverse'
                  : 'text-content-level-300 dark:text-content-level-600'
              "
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
              />
            </svg>
          </div>

          <span class="dark:text-content-level-400 text-content-level-500 text-sm">
            {{ formatReviewDate(review) }}
          </span>
        </div>

        <!-- Review text -->
        <p
          v-if="review.review"
          class="dark:text-content-level-300 leading-relaxed mt-3 text-content-level-700 text-sm"
        >
          {{ review.review }}
        </p>

        <!-- Google Maps link -->
        <a
          v-if="getGoogleMapsUri(review)"
          :href="getGoogleMapsUri(review) ?? ''"
          target="_blank"
          rel="noopener noreferrer"
          class="gap-1 hover:underline inline-flex items-center mt-3 text-primary text-xs"
          >{{ $t('commerce.storeReviews.viewOnGoogle')
          }}<svg
            class="h-3 w-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </a>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-if="!isCurrentSourceLoading && activeReviews.length === 0"
      class="py-12 text-center"
    >
      <p class="dark:text-content-level-400 text-content-level-500">
        {{
          reviewsRatingFilter
            ? $t('commerce.storeReviews.emptyRatingReviews', { rating: reviewsRatingFilter })
            : $t('commerce.storeReviews.emptyReviews')
        }}
      </p>
    </div>

    <!-- Load more -->
    <div
      v-if="!isCurrentSourceLoading && activePage < activeLastPage && activeReviews.length > 0"
      class="pt-6 text-center"
    >
      <button
        type="button"
        class="border border-border-level-300 cursor-pointer dark:border-border-level-600 dark:hover:bg-surface-level-800 dark:text-content-level-300 font-medium hover:bg-surface-level-50 px-6 py-2.5 rounded-full text-content-level-700 text-sm transition-colors"
        @click="loadMoreReviews"
      >
        {{ $t('commerce.storeReviews.loadMoreReviews') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useApiClient } from '~/services/api'
import { AppProblemModel } from '~/services/api/AppProblemModel'
import { resolveProblemMessageWithReference } from '~/services/api/resolveProblemMessageWithReference'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import type {
  GoogleReviewEntry,
  StoreReviewGoogleReviewsSuccess,
  StoreReviewIndexSuccess,
  StoreReview,
  StoreReviewRatingDistribution
} from '~/services/api/generated'
import { ReviewSource } from '~/enums'
import { Normalization } from '~/utils/normalization'
import { ReviewRatingDistribution } from '~/utils/reviewRatingDistribution'
import { formatRelativeTime } from '~/utils/formatRelativeTime'

type NativeReviewsData = StoreReviewIndexSuccess['data']
type NativeReview = NativeReviewsData['reviews'][number]
type GoogleReviewsData = StoreReviewGoogleReviewsSuccess['data']
type GoogleReview = GoogleReviewEntry

interface ReviewStats {
  average_rating: number
  rating_distribution: StoreReviewRatingDistribution
  total_reviews: number
}

const props = defineProps<{
  /** Store slug used to load reviews. */
  slug: string
}>()

// Native reviews state
const reviews = ref<NativeReview[]>([])
const reviewsStats = ref<ReviewStats | null>(null)
const reviewsLoading = ref(false)
const reviewsPage = ref(1)
const reviewsLastPage = ref(1)

// Google reviews state
const googleReviews = ref<GoogleReview[]>([])
const googleStats = ref<ReviewStats | null>(null)
const googleLoading = ref(false)
const googlePage = ref(1)
const googleLastPage = ref(1)

// Shared state
const reviewsRatingFilter = ref<number | null>(null)
const activeSource = ref<ReviewSource>(ReviewSource.NATIVE)

const { stores: storesApi } = useApiClient()
const toast = useToast()
const t = usePresentationTranslator()

// Computed: availability
const hasNativeReviews = computed(
  () => reviews.value.length > 0 || (reviewsStats.value?.total_reviews ?? 0) > 0
)
const hasGoogleReviews = computed(
  () => googleReviews.value.length > 0 || (googleStats.value?.total_reviews ?? 0) > 0
)
const hasAnyReviews = computed(() => hasNativeReviews.value || hasGoogleReviews.value)

// Computed: active source data
const activeReviews = computed(() =>
  activeSource.value === ReviewSource.GOOGLE ? googleReviews.value : reviews.value
)
const activeStats = computed<ReviewStats>(() =>
  activeSource.value === ReviewSource.GOOGLE
    ? (googleStats.value ?? {
        average_rating: 0,
        rating_distribution: ReviewRatingDistribution.EMPTY,
        total_reviews: 0
      })
    : (reviewsStats.value ?? {
        average_rating: 0,
        rating_distribution: ReviewRatingDistribution.EMPTY,
        total_reviews: 0
      })
)
const activePage = computed(() =>
  activeSource.value === ReviewSource.GOOGLE ? googlePage.value : reviewsPage.value
)
const activeLastPage = computed(() =>
  activeSource.value === ReviewSource.GOOGLE ? googleLastPage.value : reviewsLastPage.value
)
const isCurrentSourceLoading = computed(() =>
  activeSource.value === ReviewSource.GOOGLE ? googleLoading.value : reviewsLoading.value
)

const projectNativeStats = (stats: NativeReviewsData['store_stats']): ReviewStats => ({
  average_rating: stats.average_rating,
  rating_distribution: stats.rating_distribution,
  total_reviews: stats.total_reviews
})

const projectGoogleStats = (stats: GoogleReviewsData['store_stats']): ReviewStats => ({
  average_rating: stats.average_rating,
  rating_distribution: stats.rating_distribution,
  total_reviews: stats.total_reviews
})

const isGoogleReviewEntry = (entry: GoogleReviewEntry | StoreReview): entry is GoogleReviewEntry =>
  'google_maps_uri' in entry

// The endpoint returns native-shaped data with no reviews when Google reviews are disabled.
const projectGoogleReviews = (items: GoogleReviewsData['reviews']): GoogleReview[] =>
  items.filter(isGoogleReviewEntry)

// Fetch native reviews
const fetchReviews = async (reset = false, requestedPage = reset ? 1 : reviewsPage.value) => {
  if (props.slug == null || props.slug === '') return
  reviewsLoading.value = true
  try {
    const params: { rating?: number; page?: number } = { page: requestedPage }
    if (reviewsRatingFilter.value != null) params.rating = reviewsRatingFilter.value
    const response = await storesApi().getStoreReviews(props.slug, params)
    const data = response.data
    const nextReviews =
      reset || requestedPage === 1 ? data.reviews : [...reviews.value, ...data.reviews]
    const nextPage = data.pagination.current_page
    const nextStats = projectNativeStats(data.store_stats)
    const nextLastPage = data.pagination.last_page

    reviews.value = nextReviews
    reviewsPage.value = nextPage
    reviewsStats.value = nextStats
    reviewsLastPage.value = nextLastPage
  } catch (error: unknown) {
    const problem = AppProblemModel.normalize(error)
    toast.error(
      resolveProblemMessageWithReference(problem, t, t('commerce.storeReviews.unableToLoadReviews'))
    )
  } finally {
    reviewsLoading.value = false
  }
}

// Fetch Google reviews
const fetchGoogleReviews = async (reset = false, requestedPage = reset ? 1 : googlePage.value) => {
  if (props.slug == null || props.slug === '') return
  googleLoading.value = true
  try {
    const params: { rating?: number; page?: number } = { page: requestedPage }
    if (reviewsRatingFilter.value != null) params.rating = reviewsRatingFilter.value
    const response = await storesApi().getStoreGoogleReviews(props.slug, params)
    const data = response.data
    const projectedReviews = projectGoogleReviews(data.reviews)
    const nextReviews =
      reset || requestedPage === 1
        ? projectedReviews
        : [...googleReviews.value, ...projectedReviews]
    const nextStats = projectGoogleStats(data.store_stats)

    googleReviews.value = nextReviews
    googlePage.value = requestedPage
    googleStats.value = nextStats
    googleLastPage.value = data.pagination.last_page
  } catch (error: unknown) {
    const problem = AppProblemModel.normalize(error)
    toast.error(
      resolveProblemMessageWithReference(
        problem,
        t,
        t('commerce.storeReviews.unableToLoadGoogleReviews')
      )
    )
  } finally {
    googleLoading.value = false
  }
}

// Fetch all reviews (both sources)
const fetchAllReviews = async (reset = false) => {
  await Promise.all([fetchReviews(reset), fetchGoogleReviews(reset)])
  resolveDefaultSource()
}

// Determine default source based on available data
const resolveDefaultSource = () => {
  if (
    activeSource.value === ReviewSource.NATIVE &&
    !hasNativeReviews.value &&
    hasGoogleReviews.value
  ) {
    activeSource.value = ReviewSource.GOOGLE
  } else if (
    activeSource.value === ReviewSource.GOOGLE &&
    !hasGoogleReviews.value &&
    hasNativeReviews.value
  ) {
    activeSource.value = ReviewSource.NATIVE
  }
}

const switchSource = (source: ReviewSource) => {
  activeSource.value = source
}

const showNativeReviews = () => {
  switchSource(ReviewSource.NATIVE)
}
const showGoogleReviews = () => {
  switchSource(ReviewSource.GOOGLE)
}
const clearRatingFilter = () => {
  setRatingFilter(null)
}

const setRatingFilterFromEvent = (event: Event) => {
  if (!(event.currentTarget instanceof HTMLButtonElement)) return

  const rating = Number(event.currentTarget.dataset['rating'])
  setRatingFilter(Normalization.isIntegerValue(rating) ? rating : null)
}

const setRatingFilter = (rating: number | null) => {
  reviewsRatingFilter.value = rating
  if (activeSource.value === ReviewSource.GOOGLE) {
    void fetchGoogleReviews(true)
  } else {
    void fetchReviews(true)
  }
}

const loadMoreReviews = () => {
  if (activeSource.value === ReviewSource.GOOGLE) {
    if (googlePage.value < googleLastPage.value) {
      void fetchGoogleReviews(false, googlePage.value + 1)
    }
  } else {
    if (reviewsPage.value < reviewsLastPage.value) {
      void fetchReviews(false, reviewsPage.value + 1)
    }
  }
}

const formatDate = (value?: string | null) => {
  if (value == null || value === '') return ''
  return new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  }).format(new Date(value))
}

const formatReviewDate = (review: NativeReview | GoogleReview) => {
  if ('source' in review && review.source === 'google') {
    return formatRelativeTime(review.reviewed_at ?? review.created_at)
  }
  return formatDate(review.reviewed_at ?? review.created_at)
}

const getGoogleMapsUri = (review: NativeReview | GoogleReview): string | null =>
  'google_maps_uri' in review && typeof review.google_maps_uri === 'string'
    ? review.google_maps_uri
    : null

const getReviewKey = (review: NativeReview | GoogleReview): number | string =>
  review.id ?? getGoogleMapsUri(review) ?? Math.random()

onMounted(async () => {
  if (props.slug != null && props.slug !== '') {
    await fetchAllReviews(true)
  }
})

watch(
  () => props.slug,
  async (newSlug) => {
    if (newSlug != null && newSlug !== '') {
      await fetchAllReviews(true)
    }
  }
)

defineExpose({
  fetchGoogleReviews,
  fetchReviews,
  googleReviews,
  googleStats,
  reviews,
  reviewsStats
})
</script>
